// src/components/TopNav.tsx - ENHANCED: Permission-aware navigation with billing integration
import React, { memo, useState } from 'react';
import {
  Search,
  List,
  Database,
  User,
  Settings,
  LogOut,
  Activity
} from 'lucide-react';
import { useAppContext } from '../hooks/useAppContext';
import { useAuth } from '../hooks/useAuth';
import { useFirebaseAuth } from '../hooks/useFirebaseAuth';
import type { DataSourceType } from '../types';
import './top-nav.css';
import icon from '../assets/icon.png';

interface TopNavProps {
  onFilterToggle: () => void;
  filterSidebarOpen: boolean;
}

interface MenuItem {
  id: string;
  label: string;
  icon: React.ComponentType<{ size?: number }>;
  action?: () => void;
  isActive?: boolean;
  requiresPermission?: string;
}

const TopNav: React.FC<TopNavProps> = memo(() => {
  const { state, setDataSource, setView } = useAppContext();
  const { signOutUser } = useFirebaseAuth();
  const { user, hasPermission, logout } = useAuth();
  
  console.log(`🔍 TopNav Debug - User: ${user?.name || 'null'}, Permissions: ${JSON.stringify(user?.permissions)}, State: ${state.currentView}, DataSource: ${state.searchState.dataSource}`);
  
  const [expandedDropdown, setExpandedDropdown] = useState<string | null>(null);

  // Close dropdown when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (expandedDropdown && !(event.target as Element).closest('.nav-dropdown')) {
        setExpandedDropdown(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [expandedDropdown]);

  // Toggle dropdown
  const toggleDropdown = (dropdownId: string) => {
    setExpandedDropdown(expandedDropdown === dropdownId ? null : dropdownId);
  };

  // Handle data source change
  const handleDataSourceChange = (dataSource: DataSourceType) => {
    console.log(`🔄 TopNav: Changing data source to ${dataSource}`);
    setView('main');
    setDataSource(dataSource);
    setExpandedDropdown(null);
  };

  // Get user display info
  const getUserDisplayInfo = () => {
    if (user) {
      const displayName = user.name || 'User';
      const initials = displayName
        .split(' ')
        .map((part: string) => part.charAt(0).toUpperCase())
        .slice(0, 2)
        .join('');
      
      return {
        initials: initials || 'U',
        name: displayName,
        permissions: user.permissions || []
      };
    }
    
    return {
      initials: 'U',
      name: 'User',
      permissions: []
    };
  };

  const userInfo = getUserDisplayInfo();

  // Handle logout for both demo and Firebase users - always redirect to demo landing
  const handleLogout = () => {
    const isDemoUser = user?.api_key === 'demo';
    
    if (isDemoUser) {
      // For demo users, clear auth state and reload to demo landing
      console.log('🔓 Demo user logging out - redirecting to demo search page');
      logout(); // This will clear the AuthContext
      // Clear any login page flags to ensure demo mode loads
      localStorage.removeItem('show_login_page');
      localStorage.removeItem('needs_email_verification');
      // Set view to search before reload
      setView('main');
      setDataSource('people');
      window.location.reload();
    } else {
      // For Firebase users, sign out and redirect to demo landing
      console.log('🔓 Firebase user logging out - redirecting to demo search page');
      // Set view to search page before signout
      setView('main');
      setDataSource('people');
      signOutUser();
      // Clear any login page flags to ensure demo mode loads after Firebase signout
      localStorage.removeItem('show_login_page');
      localStorage.removeItem('needs_email_verification');
    }
  };

  // Handle login (for demo users to switch to real account)
  const handleLogin = () => {
    console.log('🔐 Demo user requesting login page');
    // Clear demo auth state and set flag, then reload immediately to prevent UI flicker
    localStorage.removeItem('ppl_contact_api_key');
    localStorage.removeItem('auth_user_data');
    localStorage.setItem('show_login_page', 'true');
    window.location.reload();
  };

  // Simplified menu structure - direct routing
  const menuItems: MenuItem[] = [
    {
      id: 'search',
      label: 'Search',
      icon: Search,
      action: () => handleDataSourceChange('people'),
      isActive: state.currentView === 'main' && state.searchState.dataSource === 'people',
      requiresPermission: 'search'
    },
    {
      id: 'lists',
      label: 'Lists',
      icon: List,
      action: () => handleDataSourceChange('contacts'),
      isActive: state.currentView === 'main' && state.searchState.dataSource === 'contacts',
      requiresPermission: 'contacts'
    },
    {
      id: 'api',
      label: 'API',
      icon: Database,
      action: () => window.open('/api/docs', '_blank'),
      requiresPermission: 'admin'
    }
  ];

  // Filter menu items based on permissions
  const filteredMenuItems = menuItems.filter(item => 
    !item.requiresPermission || hasPermission(item.requiresPermission)
  );

  console.log(`🔍 TopNav: Menu items - All: ${menuItems.length}, Filtered: ${filteredMenuItems.length}`);

  return (
    <nav className="top-nav">
      <div className="top-nav-container">
        {/* Left side - Logo and main navigation */}
        <div className="top-nav-left">
          {/* Logo */}
          <div className="top-nav-logo">
            <img
              src={icon}
              alt="App Icon"
              className="top-nav-logo-icon"
            />
            <span className="top-nav-logo-text">ppl.contact</span>
          </div>

          {/* Main navigation items */}
          <div className="top-nav-menu">
            {filteredMenuItems.map(item => {
              const IconComponent = item.icon;
              const isActive = item.isActive || false;

              return (
                <div key={item.id} className="nav-item">
                  <button
                    className={`nav-button${isActive ? ' active' : ''}`}
                    onClick={() => {
                      if (item.action) {
                        item.action();
                      }
                    }}
                  >
                    <IconComponent size={18} />
                    <span className="nav-label">{item.label}</span>
                  </button>
                </div>
              );
            })}
          </div>
        </div>

        {/* Right side - Terms, Privacy Policy, Account */}
        <div className="top-nav-right">
          
          {/* Terms */}
          <button
            className={`nav-button${state.currentView === 'terms' ? ' active' : ''}`}
            onClick={() => setView('terms')}
          >
            <span className="nav-label">Terms</span>
          </button>

          {/* Privacy Policy */}
          <button
            className={`nav-button${state.currentView === 'privacy' ? ' active' : ''}`}
            onClick={() => setView('privacy')}
          >
            <span className="nav-label">Privacy Policy</span>
          </button>

          {/* Account section - different for demo vs authenticated users */}
          {user?.api_key === 'demo' ? (
            /* Demo user gets a simple Sign In button */
            <div className="nav-item">
              <button
                className="nav-button sign-in-button"
                onClick={handleLogin}
                style={{
                  background: 'var(--primary, #3b82f6)',
                  color: 'white',
                  fontWeight: '500',
                  padding: '8px 16px',
                  borderRadius: '6px',
                  border: 'none'
                }}
              >
                Sign In
              </button>
            </div>
          ) : (
            /* Authenticated users get the account dropdown */
            <div className="nav-item">
              <button
                className={`nav-button account-button${expandedDropdown === 'account' ? ' active' : ''}`}
                onClick={() => toggleDropdown('account')}
              >
                <div className="account-avatar">
                  <span className="account-initials">{userInfo.initials}</span>
                </div>
                <span className="account-name">{userInfo.name}</span>
              </button>

              {expandedDropdown === 'account' && (
                <div className="nav-dropdown">
                  {/* Admin users see Activity, Account, Admin + Permissions + Logout */}
                  {hasPermission('admin') ? (
                  <>
                    <button
                      className={`dropdown-item${state.currentView === 'activity' ? ' active' : ''}`}
                      onClick={() => {
                        setView('activity');
                        setExpandedDropdown(null);
                      }}
                    >
                      <div className="dropdown-item-content">
                        <Activity size={16} />
                        <span className="dropdown-item-label">Activity</span>
                      </div>
                    </button>

                    <button
                      className={`dropdown-item${state.currentView === 'billing' ? ' active' : ''}`}
                      onClick={() => {
                        setView('billing');
                        setExpandedDropdown(null);
                      }}
                    >
                      <div className="dropdown-item-content">
                        <User size={16} />
                        <span className="dropdown-item-label">Account</span>
                      </div>
                    </button>

                    <button
                      className={`dropdown-item${state.currentView === 'admin' ? ' active' : ''}`}
                      onClick={() => {
                        setView('admin');
                        setExpandedDropdown(null);
                      }}
                    >
                      <div className="dropdown-item-content">
                        <Settings size={16} />
                        <span className="dropdown-item-label">Admin</span>
                      </div>
                    </button>

                    {/* User info section */}
                    <div className="dropdown-divider" />
                    <div className="dropdown-info">
                      <div className="user-permissions">
                        <strong>Permissions:</strong>
                        <div className="permission-badges">
                          {userInfo.permissions.map(permission => (
                            <span key={permission} className="permission-badge">
                              {permission}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>

                    <div className="dropdown-divider" />

                    <button
                      className="dropdown-item logout-item"
                      onClick={() => {
                        setExpandedDropdown(null);
                        handleLogout();
                      }}
                      style={{
                        color: 'var(--error, #ef4444)'
                      }}
                    >
                      <div className="dropdown-item-content">
                        <LogOut size={16} />
                        <span className="dropdown-item-label">Logout</span>
                      </div>
                    </button>
                  </>
                ) : (
                  /* Regular users see only Account and Logout */
                  <>
                    <button
                      className={`dropdown-item${state.currentView === 'billing' ? ' active' : ''}`}
                      onClick={() => {
                        setView('billing');
                        setExpandedDropdown(null);
                      }}
                    >
                      <div className="dropdown-item-content">
                        <User size={16} />
                        <span className="dropdown-item-label">Account</span>
                      </div>
                    </button>

                    <div className="dropdown-divider" />

                    <button
                      className="dropdown-item logout-item"
                      onClick={() => {
                        setExpandedDropdown(null);
                        handleLogout();
                      }}
                      style={{
                        color: 'var(--error, #ef4444)'
                      }}
                    >
                      <div className="dropdown-item-content">
                        <LogOut size={16} />
                        <span className="dropdown-item-label">Logout</span>
                      </div>
                    </button>
                  </>
                )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <style>{`
        .account-name {
          font-size: 14px;
          font-weight: 500;
          max-width: 120px;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .dropdown-divider {
          height: 1px;
          background: #e2e8f0;
          margin: 8px 0;
        }

        .dropdown-info {
          padding: 8px 16px;
          font-size: 12px;
          color: #64748b;
        }

        .user-permissions {
          margin-bottom: 8px;
        }

        .permission-badges {
          display: flex;
          flex-wrap: wrap;
          gap: 4px;
          margin-top: 4px;
        }

        .permission-badge {
          display: inline-block;
          padding: 2px 6px;
          background: #e0f2fe;
          color: #0369a1;
          border-radius: 10px;
          font-size: 10px;
          font-weight: 500;
          text-transform: uppercase;
          letter-spacing: 0.025em;
        }

        .sign-in-button {
          cursor: pointer;
          transition: background-color 0.2s ease, transform 0.1s ease;
        }

        .sign-in-button:hover {
          background: var(--primary-dark, #2563eb) !important;
          transform: translateY(-1px);
        }

        .sign-in-button:active {
          transform: translateY(0);
        }
      `}</style>
    </nav>
  );
});

TopNav.displayName = 'TopNav';

export default TopNav;